import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { getCollections } from "../../lib/investApi";
import InvestCategoryCard from "./InvestCategoryCard";
import InvestBuyModal from "./InvestBuyModal";

const CATEGORY_ORDER = ["SUV", "SEDAN", "HATCHBACK", "TRUCK", "SPORTS"];

function SkeletonCard() {
  return (
    <div className="zc-card" style={{ overflow: "hidden", padding: 0 }}>
      <div style={{ height: 160, background: "var(--zc-bg-3)", animation: "pulse 1.5s ease-in-out infinite" }} />
      <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ height: 24, background: "var(--zc-bg-3)", borderRadius: 4, width: "40%", animation: "pulse 1.5s ease-in-out infinite" }} />
        <div style={{ height: 12, background: "var(--zc-bg-3)", borderRadius: 4, width: "90%", animation: "pulse 1.5s ease-in-out infinite" }} />
        <div style={{ height: 36, background: "var(--zc-bg-3)", borderRadius: 6, marginTop: 8, animation: "pulse 1.5s ease-in-out infinite" }} />
      </div>
    </div>
  );
}

export default function InvestCollectionsTab({ walletUserId, onPurchaseComplete }) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buyCollection, setBuyCollection] = useState(null);  // { collection, displayName }


  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCollections();
      setCollections(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleBuySuccess = () => {
    setBuyCollection(null);
    load();
    onPurchaseComplete?.();
  };

  // Group collections by category; include any extra categories not in the default order
  const grouped = collections.reduce((acc, c) => {
    const cat = c.category || "OTHER";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(c);
    return acc;
  }, {});

  const categories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h2 className="zc-headline" style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Token Types</h2>
          <p style={{ color: "var(--zc-text-3)", fontSize: 13, margin: "4px 0 0" }}>
            Fractional car tokens — buy any type to start investing
          </p>
        </div>
        <button onClick={load} className="zc-btn-ghost" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 16, background: "#FEF2F2", borderRadius: 8, color: "var(--zc-danger)", marginBottom: 24, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "center" }}>
          {[1, 2, 3].map((i) => <div key={i} style={{ flex: "1 1 280px", maxWidth: 380 }}><SkeletonCard /></div>)}
        </div>
      ) : categories.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", color: "var(--zc-text-3)" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚗</div>
          <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 8, color: "var(--zc-text-2)" }}>No tokens available</p>
          <p style={{ fontSize: 14 }}>Investment collections have not been created yet.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "center" }}>
          {categories.map((cat) => (
            <div key={cat} style={{ flex: "1 1 280px", maxWidth: 380 }}>
              <InvestCategoryCard
                category={cat}
                collections={grouped[cat]}
                onBuy={(col, label) => setBuyCollection({ collection: col, displayName: `${label} Token` })}
              />
            </div>
          ))}
        </div>
      )}

      {/* Buy modal */}
      {buyCollection && (
        <InvestBuyModal
          collection={buyCollection.collection}
          displayName={buyCollection.displayName}
          walletUserId={walletUserId}
          onClose={() => setBuyCollection(null)}
          onSuccess={handleBuySuccess}
        />
      )}
    </div>
  );
}
